/**
 * Media Tools Configuration
 *
 * Defines the media type tool pages and their associated process types.
 * Used by [media-tools].tsx to generate dynamic pages.
 */

export interface MediaToolConfig {
    /** URL slug (e.g., 'image-tools') */
    slug: string;
    /** Display name (e.g., 'Image Tools') */
    name: string;
    /** Media types from selector-options.json to include */
    mediaTypes: string[];
    /** Emoji icon */
    icon: string;
    /** Short description for the page */
    description: string;
    /** Meta description for SEO */
    metaDescription: string;
}

/**
 * All media tool pages configuration
 */
export const mediaToolsConfig: MediaToolConfig[] = [
    {
        slug: 'all-tools',
        name: 'All Tools',
        mediaTypes: ['IMAGE', 'VIDEO', 'AUDIO', 'DOCUMENT', 'EBOOK', 'PRESENTATION', 'ARCHIVE', 'VECTOR', 'OTHER'],
        icon: '🧰',
        description: 'Browse all 5000+ free online file conversion, compression, and editing tools.',
        metaDescription: 'Free online file tools - convert, compress, merge, split, resize, crop and more. 5000+ tools for images, videos, audio, documents and archives.',
    },
    {
        slug: 'image-tools',
        name: 'Image Tools',
        mediaTypes: ['IMAGE'],
        icon: '🖼️',
        description: 'Convert, compress, resize, crop, rotate, and flip images in any format.',
        metaDescription: 'Free online image tools - convert, compress, resize, crop, rotate JPG, PNG, HEIC, WebP, GIF, TIFF and more.',
    },
    {
        slug: 'video-tools',
        name: 'Video Tools',
        mediaTypes: ['VIDEO'],
        icon: '🎬',
        description: 'Convert, compress, cut, and trim videos in popular formats.',
        metaDescription: 'Free online video tools - convert, compress, cut, trim MP4, MOV, AVI, MKV, WebM and more.',
    },
    {
        slug: 'audio-tools',
        name: 'Audio Tools',
        mediaTypes: ['AUDIO'],
        icon: '🎧',
        description: 'Convert, compress, cut, and trim audio files.',
        metaDescription: 'Free online audio tools - convert, compress, cut, trim MP3, WAV, FLAC, AAC, OGG and more.',
    },
    {
        slug: 'document-tools',
        name: 'Document Tools',
        mediaTypes: ['DOCUMENT', 'EBOOK', 'PRESENTATION'],
        icon: '📝',
        description: 'Convert, merge, split, and compress documents, ebooks, and presentations.',
        metaDescription: 'Free online document tools - convert, merge, split PDF, Word, PowerPoint, EPUB and more.',
    },
    {
        slug: 'archive-tools',
        name: 'Archive Tools',
        mediaTypes: ['ARCHIVE'],
        icon: '🗄️',
        description: 'Create, extract, and convert archive files.',
        metaDescription: 'Free online archive tools - create, extract, convert ZIP, RAR, 7Z, TAR and more.',
    },
    {
        slug: 'other-tools',
        name: 'Other Utilities',
        mediaTypes: ['OTHER', 'VECTOR'],
        icon: '🛠️',
        description: 'Encode, decode, and other utility tools.',
        metaDescription: 'Free online utility tools - Base64 encode/decode, URL encode/decode, word count, SVG tools and more.',
    },
    {
        slug: 'utility-tools',
        name: 'Utility Tools',
        mediaTypes: ['TOOL'],
        icon: '🧮',
        description: 'Color converter, hash generator, text case converter, and more developer utilities.',
        metaDescription: 'Free online utility tools - color converter, hash generator, text case converter, JSON/YAML/XML converter, OCR, and more.',
    },
];

/**
 * Get all media tools slugs (for routing exclusion in [feature].tsx)
 */
export const mediaToolsSlugs = mediaToolsConfig.map(config => config.slug);

/**
 * Get media tool config by slug
 */
export function getMediaToolConfigBySlug(slug: string): MediaToolConfig | undefined {
    return mediaToolsConfig.find(config => config.slug === slug);
}

/**
 * Process type display names and order
 */
export const processTypeConfig: Record<string, { displayName: string; order: number }> = {
    'CONVERT': { displayName: 'Convert', order: 1 },
    'COMPRESS': { displayName: 'Compress', order: 2 },
    'MERGE': { displayName: 'Merge', order: 3 },
    'SPLIT': { displayName: 'Split', order: 4 },
    'CUT': { displayName: 'Cut', order: 5 },
    'TRIM': { displayName: 'Trim', order: 6 },
    'CROP': { displayName: 'Crop', order: 7 },
    'RESIZE': { displayName: 'Resize', order: 8 },
    'ROTATE': { displayName: 'Rotate', order: 9 },
    'FLIP': { displayName: 'Flip', order: 10 },
    'EXTRACT': { displayName: 'Extract', order: 11 },
    'ARCHIVE': { displayName: 'Archive', order: 12 },
    'ENCODE': { displayName: 'Encode', order: 13 },
    'DECODE': { displayName: 'Decode', order: 14 },
    'GENERATE': { displayName: 'Generate', order: 15 },
    'COUNT': { displayName: 'Count', order: 16 },
    'FORMAT': { displayName: 'Format', order: 17 },
    'MINIFY': { displayName: 'Minify', order: 18 },
    'VALIDATE': { displayName: 'Validate', order: 19 },
    'TEST': { displayName: 'Test', order: 20 },
    'CALCULATE': { displayName: 'Calculate', order: 21 },
    'LOOKUP': { displayName: 'Look Up', order: 22 },
    // S6 family #10 — keys MUST match xconfig ProcessType enum values exactly
    // (no hyphens) so __tests__/process-type-config.test.ts finds each entry
    // when it iterates selector-options.json.
    'WATERMARK': { displayName: 'Watermark', order: 23 },
    'ADDPAGENUMBERS': { displayName: 'Add Page Numbers', order: 24 },
    'DELETEPAGES': { displayName: 'Delete Pages', order: 25 },
    'EXTRACTPAGES': { displayName: 'Extract Pages', order: 26 },
    'REORDER': { displayName: 'Reorder', order: 27 },
    'NUP': { displayName: 'N-Up', order: 28 },
    'FLATTEN': { displayName: 'Flatten', order: 29 },
};

/**
 * Synthesizes a MediaToolConfig header for a process-type tools page
 * (e.g. /tools/convert). These pages show every tool of one process across all
 * media types, so mediaTypes is empty; the icon is supplied by the caller from
 * the matching ProcessSection to avoid duplicating the icon map here.
 */
export function buildProcessToolConfig(processType: string, icon = '🔧'): MediaToolConfig {
    const key = processType.toUpperCase();
    const displayName = processTypeConfig[key]?.displayName
        ?? (key.charAt(0) + key.slice(1).toLowerCase());
    const lower = displayName.toLowerCase();
    return {
        slug: key.toLowerCase(),
        name: `${displayName} Tools`,
        mediaTypes: [],
        icon,
        description: `Free online ${lower} tools for images, videos, audio, documents, and more.`,
        metaDescription: `Free online ${lower} tools - ${lower} files across images, video, audio, documents, and archives.`,
    };
}
